import type { User as SupabaseUser } from '@supabase/supabase-js'

import { useSupabaseUser } from '~/composables/useSupabaseUser'
import { convertUserProfile, type UserProfile } from '~/entities/user'
import { getUsers } from '~/repositories/userRepository'

const convertToCurrentUser = (user: UserProfile) => {
  return {
    ...user,
    canEditOrganization: user.role === 'ORGANIZATION_ADMIN',
    canEditLocation: user.role === 'ORGANIZATION_ADMIN' || user.role === 'LOCATION_ADMIN',
    enabledMyPageNotifications:
      user.user_email_settings?.enabled_my_page_notifications ?? user.enabled_email_notification,
    enabledAssignedTicketNotifications: user.user_email_settings?.enabled_assigned_ticket_notifications ?? true,
    enabledDailyVerifierNotifications: user.user_email_settings?.enabled_daily_verifier_notifications ?? true,
  }
}

const recoverUser = (supabaseUser: Ref<SupabaseUser | null>, userState: Ref<UserProfile | null>) => async () => {
  if (supabaseUser.value) {
    const data = await getUsers({ accountId: supabaseUser.value.id })

    if (data.length === 0) {
      throw new Error('user not found')
    }

    userState.value = convertUserProfile(data[0])
  }
}

export const useCurrentUser = () => {
  const supabaseUser = useSupabaseUser()
  const user = useState<UserProfile | null>('user')

  const currentUser = computed(() => {
    return user.value ? convertToCurrentUser(user.value) : null
  })

  return {
    currentUser,
    recoverUser: recoverUser(supabaseUser, user),
    updateCurrentUser: (updatedUser: UserProfile | null) => (user.value = updatedUser),
  }
}
